"use client";

import { useEffect, useState } from "react";
import { Bell, X, Smartphone } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { useAuth } from "@/hooks/useAuth";
import {
  detectPlatform,
  iosNeedsInstall,
  isStandalone,
  pushSupported,
} from "@/lib/pwa/installDetect";
import { subscribeToPush } from "@/lib/pwa/pushClient";
import { IosInstallGuide } from "./IosInstallGuide";

const DISMISS_KEY = "cp-push-banner-dismissed";

/**
 * Decision #4: dezent — shown ONCE after login. If dismissed, never shown
 * again unless the user clears localStorage. The Settings page is the
 * canonical place to manage push devices afterwards.
 */
export function PushPermissionBanner() {
  const { user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    // iOS Safari (not installed as PWA) has no Notification global — show
    // the banner so we can guide the user to install. Accessing
    // Notification.permission here would throw a ReferenceError.
    if (iosNeedsInstall()) {
      setVisible(true);
      return;
    }

    if (!pushSupported()) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted") return;
    if (Notification.permission === "denied") return; // would just annoy
    setVisible(true);
  }, [user, loading]);

  if (!visible) {
    return showIosGuide ? (
      <IosInstallGuide onClose={() => setShowIosGuide(false)} />
    ) : null;
  }

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const onEnable = async () => {
    setBusy(true);
    try {
      // iOS without standalone → show install guide instead of permission prompt
      if (iosNeedsInstall()) {
        setShowIosGuide(true);
        dismiss();
        return;
      }
      const result = await subscribeToPush();
      if (result.ok) {
        dismiss();
      } else if (result.reason === "ios_needs_install") {
        setShowIosGuide(true);
        dismiss();
      } else {
        // permission denied or unsupported — just dismiss, user can retry in settings
        dismiss();
      }
    } finally {
      setBusy(false);
    }
  };

  const ios = detectPlatform() === "ios" && !isStandalone();
  const Icon = ios ? Smartphone : Bell;

  return (
    <>
      <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-4 right-4 z-40 mx-auto max-w-md rounded-2xl border border-border bg-card/95 p-4 shadow-lg backdrop-blur lg:bottom-4 lg:left-auto lg:right-4">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon className="size-5" />
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-sm font-semibold">
              {ios ? "Push auf iPhone aktivieren" : "Benachrichtigungen aktivieren"}
            </p>
            <p className="text-xs text-foreground-subtle">
              {ios
                ? "Installiere Community Plan auf dem Home-Bildschirm, um Push-Mitteilungen zu erhalten."
                : "Bleibe über neue Events, Anmeldungen und Mitteilungen informiert."}
            </p>
            <div className="flex items-center gap-2 pt-2">
              <Button size="sm" onClick={onEnable} disabled={busy}>
                {ios ? "Anleitung anzeigen" : busy ? "Anfrage…" : "Aktivieren"}
              </Button>
              <button
                type="button"
                onClick={dismiss}
                className="text-xs text-foreground-subtle hover:underline"
              >
                Später
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Schließen"
            className="text-foreground-subtle hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
      {showIosGuide && <IosInstallGuide onClose={() => setShowIosGuide(false)} />}
    </>
  );
}
